import { keepPreviousData, useInfiniteQuery, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  fetchNotifications,
  fetchUnreadNotificationCount,
  markNotificationAsRead,
  markAllNotificationsAsRead,
  deleteNotification,
  fetchUnifiedNotifications,
  fetchUnifiedUnreadCount,
  fetchUnifiedNotification,
  markUnifiedNotificationAsRead,
  markAllUnifiedNotificationsAsRead,
  markUnifiedNotificationsForTargetAsRead,
  deleteUnifiedNotification,
  type NotificationItem,
  type NotificationListParams,
  type NotificationListResponse,
} from '../../lib/api/notifications';
import type { HeartbeatData } from '../../lib/api/heartbeat';
import { dismissWebPushByTag, dismissAllWebPush } from '../../lib/notifications/dismissWebPush';
import { queryKeys } from './queryKeys';

export const useNotifications = (token: string | null, projectId: string, params: NotificationListParams = {}) => {
  return useQuery({
    queryKey: queryKeys.notifications.list(projectId, {
      page: params.page,
      limit: params.limit,
      status: params.status,
      type: params.type,
    }),
    queryFn: () => fetchNotifications(token as string, projectId, params),
    enabled: Boolean(token) && projectId.length > 0,
    refetchOnWindowFocus: true,
    refetchOnReconnect: true,
    placeholderData: keepPreviousData,
  });
};

export const useUnreadNotificationCount = (token: string | null, projectId: string) => {
  return useQuery({
    queryKey: queryKeys.notifications.unreadCount(projectId),
    queryFn: () => fetchUnreadNotificationCount(token as string, projectId),
    enabled: Boolean(token) && projectId.length > 0,
    refetchOnWindowFocus: true,
    refetchOnReconnect: true,
  });
};

export const useNotificationsInfinite = (
  token: string | null,
  projectId: string,
  params: Omit<NotificationListParams, 'page' | 'limit'> = {},
) => {
  return useInfiniteQuery<
    NotificationListResponse,
    Error,
    NotificationListResponse,
    ReturnType<typeof queryKeys.notifications.infinite>,
    number
  >({
    queryKey: queryKeys.notifications.infinite(projectId, params),
    queryFn: ({ pageParam }) =>
      fetchNotifications(token as string, projectId, { ...params, page: pageParam, limit: 20 }),
    initialPageParam: 1,
    getNextPageParam: (lastPage) =>
      lastPage.pagination.page < lastPage.pagination.totalPages ? lastPage.pagination.page + 1 : undefined,
    enabled: Boolean(token) && projectId.length > 0,
    refetchOnWindowFocus: true,
    refetchOnReconnect: true,
  });
};

export const useMarkNotificationAsReadMutation = (token: string | null, projectId: string) => {
  const queryClient = useQueryClient();

  type NotificationCacheSnapshot = [
    queryKey: readonly unknown[],
    data: NotificationListResponse | { pages: Array<NotificationListResponse>; pageParams: unknown[] } | undefined,
  ][];

  return useMutation<
    NotificationItem,
    Error,
    { id: string; entityId?: string | null; type?: string },
    { previousQueries: NotificationCacheSnapshot; previousUnreadCount: number | undefined }
  >({
    mutationFn: ({ id }) => markNotificationAsRead(token as string, projectId, id),
    onMutate: async ({ id }) => {
      await queryClient.cancelQueries({ queryKey: queryKeys.notifications.all(projectId) });

      const previousQueries = queryClient.getQueriesData<
        NotificationListResponse | { pages: Array<NotificationListResponse>; pageParams: unknown[] }
      >({ queryKey: queryKeys.notifications.all(projectId) });
      const previousUnreadCount = queryClient.getQueryData<number>(queryKeys.notifications.unreadCount(projectId));

      const updateItem = (item: NotificationItem) => (item.id === id ? { ...item, status: 'READ' as const } : item);

      queryClient.setQueriesData<
        NotificationListResponse | { pages: Array<NotificationListResponse>; pageParams: unknown[] }
      >({ queryKey: queryKeys.notifications.all(projectId) }, (old) => {
        if (!old || typeof old !== 'object') return old;
        if ('pages' in old) {
          return {
            ...old,
            pages: old.pages.map((page) => ({
              ...page,
              data: page.data.map(updateItem),
            })),
          };
        }
        if ('data' in old && Array.isArray(old.data)) {
          return { ...old, data: old.data.map(updateItem) };
        }
        return old;
      });

      if (typeof previousUnreadCount === 'number' && previousUnreadCount > 0) {
        queryClient.setQueryData<number>(queryKeys.notifications.unreadCount(projectId), previousUnreadCount - 1);
      }

      return { previousQueries, previousUnreadCount };
    },
    onSuccess: (_data, variables) => {
      if (variables.entityId && variables.type) {
        void dismissWebPushByTag(`${variables.type}:${variables.entityId}`);
      }
    },
    onError: (_error, _variables, context) => {
      if (context?.previousQueries) {
        for (const [key, data] of context.previousQueries) {
          queryClient.setQueryData(key, data);
        }
      }
      if (typeof context?.previousUnreadCount === 'number') {
        queryClient.setQueryData(queryKeys.notifications.unreadCount(projectId), context.previousUnreadCount);
      }
    },
    onSettled: () => {
      void queryClient.invalidateQueries({
        queryKey: queryKeys.notifications.all(projectId),
      });
    },
  });
};

export const useMarkAllNotificationsAsReadMutation = (token: string | null, projectId: string) => {
  const queryClient = useQueryClient();

  return useMutation<
    number,
    Error,
    void,
    {
      previousQueries: [
        queryKey: readonly unknown[],
        data: NotificationListResponse | { pages: Array<NotificationListResponse>; pageParams: unknown[] } | undefined,
      ][];
      previousUnreadCount: number | undefined;
      previousHeartbeat: HeartbeatData | undefined;
    }
  >({
    mutationFn: () => markAllNotificationsAsRead(token as string, projectId),
    onMutate: async () => {
      await queryClient.cancelQueries({ queryKey: queryKeys.notifications.all(projectId) });
      await queryClient.cancelQueries({ queryKey: queryKeys.heartbeat.all(projectId) });

      const previousQueries = queryClient.getQueriesData<
        NotificationListResponse | { pages: Array<NotificationListResponse>; pageParams: unknown[] }
      >({ queryKey: queryKeys.notifications.all(projectId) });
      const previousUnreadCount = queryClient.getQueryData<number>(queryKeys.notifications.unreadCount(projectId));
      const previousHeartbeat = queryClient.getQueryData<HeartbeatData>(queryKeys.heartbeat.all(projectId));

      const markRead = (item: NotificationItem): NotificationItem => ({ ...item, status: 'READ' });

      queryClient.setQueriesData<
        NotificationListResponse | { pages: Array<NotificationListResponse>; pageParams: unknown[] }
      >({ queryKey: queryKeys.notifications.all(projectId) }, (old) => {
        if (!old || typeof old !== 'object') return old;
        if ('pages' in old) {
          return {
            ...old,
            pages: old.pages.map((page) => ({
              ...page,
              data: page.data.map(markRead),
            })),
          };
        }
        if ('data' in old && Array.isArray(old.data)) {
          return { ...old, data: old.data.map(markRead) };
        }
        return old;
      });

      queryClient.setQueryData<number>(queryKeys.notifications.unreadCount(projectId), 0);

      if (previousHeartbeat) {
        queryClient.setQueryData<HeartbeatData>(queryKeys.heartbeat.all(projectId), {
          ...previousHeartbeat,
          unreadNotificationCount: 0,
          recentNotifications: previousHeartbeat.recentNotifications.map((n) => ({ ...n, status: 'READ' })),
        });
      }

      return { previousQueries, previousUnreadCount, previousHeartbeat };
    },
    onSuccess: () => {
      void dismissAllWebPush();
    },
    onError: (_error, _variables, context) => {
      if (context?.previousQueries) {
        for (const [key, data] of context.previousQueries) {
          queryClient.setQueryData(key, data);
        }
      }
      if (typeof context?.previousUnreadCount === 'number') {
        queryClient.setQueryData(queryKeys.notifications.unreadCount(projectId), context.previousUnreadCount);
      }
      if (context?.previousHeartbeat) {
        queryClient.setQueryData(queryKeys.heartbeat.all(projectId), context.previousHeartbeat);
      }
    },
    onSettled: () => {
      void queryClient.invalidateQueries({
        queryKey: queryKeys.notifications.all(projectId),
      });
      void queryClient.invalidateQueries({
        queryKey: queryKeys.heartbeat.all(projectId),
      });
    },
  });
};

export const useDeleteNotificationMutation = (token: string | null, projectId: string) => {
  const queryClient = useQueryClient();

  return useMutation<void, Error, string>({
    mutationFn: (id) => deleteNotification(token as string, projectId, id),
    onSettled: () => {
      void queryClient.invalidateQueries({
        queryKey: queryKeys.notifications.all(projectId),
      });
    },
  });
};

// --- Unified (member-scoped) hooks ---

export const useUnifiedNotifications = (token: string | null, params: NotificationListParams = {}) => {
  return useQuery({
    queryKey: queryKeys.notifications.unified.list({
      page: params.page,
      limit: params.limit,
      status: params.status,
      type: params.type,
    }),
    queryFn: () => fetchUnifiedNotifications(token as string, params),
    enabled: Boolean(token),
    refetchOnWindowFocus: true,
    refetchOnReconnect: true,
    placeholderData: keepPreviousData,
  });
};

export const useUnifiedUnreadCount = (token: string | null) => {
  return useQuery({
    queryKey: queryKeys.notifications.unified.unreadCount,
    queryFn: () => fetchUnifiedUnreadCount(token as string),
    enabled: Boolean(token),
    refetchOnWindowFocus: true,
    refetchOnReconnect: true,
  });
};

export const useUnifiedNotification = (token: string | null, id: string | null) => {
  return useQuery({
    queryKey: queryKeys.notifications.unified.detail(id ?? ''),
    queryFn: () => fetchUnifiedNotification(token as string, id as string),
    enabled: Boolean(token) && Boolean(id),
    refetchOnWindowFocus: true,
    refetchOnReconnect: true,
  });
};

export const useUnifiedNotificationsInfinite = (
  token: string | null,
  params: Omit<NotificationListParams, 'page' | 'limit'> = {},
) => {
  return useInfiniteQuery<
    NotificationListResponse,
    Error,
    NotificationListResponse,
    ReturnType<typeof queryKeys.notifications.unified.infinite>,
    number
  >({
    queryKey: queryKeys.notifications.unified.infinite(params),
    queryFn: ({ pageParam }) => fetchUnifiedNotifications(token as string, { ...params, page: pageParam, limit: 20 }),
    initialPageParam: 1,
    getNextPageParam: (lastPage) =>
      lastPage.pagination.page < lastPage.pagination.totalPages ? lastPage.pagination.page + 1 : undefined,
    enabled: Boolean(token),
    refetchOnWindowFocus: true,
    refetchOnReconnect: true,
  });
};

export const useMarkUnifiedNotificationAsReadMutation = (token: string | null) => {
  const queryClient = useQueryClient();

  type NotificationCacheSnapshot = [
    queryKey: readonly unknown[],
    data: NotificationListResponse | { pages: Array<NotificationListResponse>; pageParams: unknown[] } | undefined,
  ][];

  return useMutation<
    NotificationItem,
    Error,
    { id: string; entityId?: string | null; type?: string },
    { previousQueries: NotificationCacheSnapshot; previousUnreadCount: number | undefined }
  >({
    mutationFn: ({ id }) => markUnifiedNotificationAsRead(token as string, id),
    onMutate: async ({ id }) => {
      await queryClient.cancelQueries({ queryKey: queryKeys.notifications.unified.all });

      const previousQueries = queryClient.getQueriesData<
        NotificationListResponse | { pages: Array<NotificationListResponse>; pageParams: unknown[] }
      >({ queryKey: queryKeys.notifications.unified.all });
      const previousUnreadCount = queryClient.getQueryData<number>(queryKeys.notifications.unified.unreadCount);

      const updateItem = (item: NotificationItem) => (item.id === id ? { ...item, status: 'READ' as const } : item);

      queryClient.setQueriesData<
        NotificationListResponse | { pages: Array<NotificationListResponse>; pageParams: unknown[] }
      >({ queryKey: queryKeys.notifications.unified.all }, (old) => {
        if (!old || typeof old !== 'object') return old;
        if ('pages' in old) {
          return {
            ...old,
            pages: old.pages.map((page) => ({
              ...page,
              data: page.data.map(updateItem),
            })),
          };
        }
        if ('data' in old && Array.isArray(old.data)) {
          return { ...old, data: old.data.map(updateItem) };
        }
        return old;
      });

      if (typeof previousUnreadCount === 'number' && previousUnreadCount > 0) {
        queryClient.setQueryData<number>(queryKeys.notifications.unified.unreadCount, previousUnreadCount - 1);
      }

      return { previousQueries, previousUnreadCount };
    },
    onSuccess: (_data, variables) => {
      if (variables.entityId && variables.type) {
        void dismissWebPushByTag(`${variables.type}:${variables.entityId}`);
      }
    },
    onError: (_error, _variables, context) => {
      if (context?.previousQueries) {
        for (const [key, data] of context.previousQueries) {
          queryClient.setQueryData(key, data);
        }
      }
      if (typeof context?.previousUnreadCount === 'number') {
        queryClient.setQueryData(queryKeys.notifications.unified.unreadCount, context.previousUnreadCount);
      }
    },
    onSettled: () => {
      void queryClient.invalidateQueries({
        queryKey: queryKeys.notifications.unified.all,
      });
    },
  });
};

export const useMarkAllUnifiedNotificationsAsReadMutation = (token: string | null) => {
  const queryClient = useQueryClient();

  return useMutation<
    number,
    Error,
    void,
    {
      previousQueries: [
        queryKey: readonly unknown[],
        data: NotificationListResponse | { pages: Array<NotificationListResponse>; pageParams: unknown[] } | undefined,
      ][];
      previousUnreadCount: number | undefined;
    }
  >({
    mutationFn: () => markAllUnifiedNotificationsAsRead(token as string),
    onMutate: async () => {
      await queryClient.cancelQueries({ queryKey: queryKeys.notifications.unified.all });

      const previousQueries = queryClient.getQueriesData<
        NotificationListResponse | { pages: Array<NotificationListResponse>; pageParams: unknown[] }
      >({ queryKey: queryKeys.notifications.unified.all });
      const previousUnreadCount = queryClient.getQueryData<number>(queryKeys.notifications.unified.unreadCount);

      const markRead = (item: NotificationItem): NotificationItem => ({ ...item, status: 'READ' });

      queryClient.setQueriesData<
        NotificationListResponse | { pages: Array<NotificationListResponse>; pageParams: unknown[] }
      >({ queryKey: queryKeys.notifications.unified.all }, (old) => {
        if (!old || typeof old !== 'object') return old;
        if ('pages' in old) {
          return {
            ...old,
            pages: old.pages.map((page) => ({
              ...page,
              data: page.data.map(markRead),
            })),
          };
        }
        if ('data' in old && Array.isArray(old.data)) {
          return { ...old, data: old.data.map(markRead) };
        }
        return old;
      });

      queryClient.setQueryData<number>(queryKeys.notifications.unified.unreadCount, 0);

      return { previousQueries, previousUnreadCount };
    },
    onSuccess: () => {
      void dismissAllWebPush();
    },
    onError: (_error, _variables, context) => {
      if (context?.previousQueries) {
        for (const [key, data] of context.previousQueries) {
          queryClient.setQueryData(key, data);
        }
      }
      if (typeof context?.previousUnreadCount === 'number') {
        queryClient.setQueryData(queryKeys.notifications.unified.unreadCount, context.previousUnreadCount);
      }
    },
    onSettled: () => {
      void queryClient.invalidateQueries({
        queryKey: queryKeys.notifications.unified.all,
      });
    },
  });
};

export const useMarkUnifiedNotificationsForTargetAsReadMutation = (token: string | null) => {
  const queryClient = useQueryClient();

  return useMutation<number, Error, { entityId: string }>({
    mutationFn: ({ entityId }) => markUnifiedNotificationsForTargetAsRead(token as string, entityId),
    onSuccess: (count) => {
      const previousUnreadCount = queryClient.getQueryData<number>(queryKeys.notifications.unified.unreadCount);
      if (typeof previousUnreadCount === 'number') {
        queryClient.setQueryData<number>(
          queryKeys.notifications.unified.unreadCount,
          Math.max(0, previousUnreadCount - count),
        );
      }
    },
    onSettled: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.notifications.unified.all });
    },
  });
};

export const useDeleteUnifiedNotificationMutation = (token: string | null) => {
  const queryClient = useQueryClient();

  return useMutation<void, Error, string>({
    mutationFn: (id) => deleteUnifiedNotification(token as string, id),
    onSettled: () => {
      void queryClient.invalidateQueries({
        queryKey: queryKeys.notifications.unified.all,
      });
    },
  });
};
